import * as React from 'react';

import { captureException } from '@sentry/browser';
import { isAxiosError } from '~api/isAxiosError';

import { translate } from '~l10n/client';
import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useGetWorkspaceDetail } from '~teacher/hook/workspaces';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';
import { useRemoveWorkspaceMember } from '~teacher/hook/workspaces/useRemoveWorkspaceMember';

interface LeaveLibraryDialogProps {
  workspaceId: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

/**
 * Confirms a member leaving the library, then reuses the existing self-removal
 * flow — `removeUserFromWorkspace` allows self-removal regardless of role
 * ("Self-removal is always allowed — skip permission check"), the same path the
 * Members tab's "Remove (self)" uses. On success the user has lost access, so
 * we route them back to Launch.
 *
 * Mirrors `ConfirmLeaveTeamDialog` (account page): `DialogConfirmation` with a
 * default Cancel `UIButton` + a `risky` confirm `UIButton`.
 */
export function LeaveLibraryDialog({
  workspaceId,
  isOpen,
  setIsOpen,
}: LeaveLibraryDialogProps) {
  const router = useSocrativeRouter();
  const workspaceDetail = useGetWorkspaceDetail({ workspaceId });
  const libraryName = workspaceDetail.data?.name ?? '';
  // `getWorkspaceRole` returns the requesting user's membership; `.id` is the
  // UserWorkspace (membership) id the remove endpoint expects.
  const requestUser = useGetWorkspaceRole({ workspaceId });
  const removeMember = useRemoveWorkspaceMember(workspaceId);
  const [isLeaving, setIsLeaving] = React.useState(false);

  const membershipId = requestUser.data?.id;
  const onClose = () => setIsOpen(false);

  const handleLeave = () => {
    if (!membershipId) return;
    setIsLeaving(true);
    removeMember.mutate(
      { workspaceId, userId: membershipId },
      {
        onSuccess: () => {
          toast.positive(translate('You have left the library.'));
          router.navigate('launch', { trigger: true, replace: true });
        },
        onError: (error) => {
          setIsLeaving(false);
          toast.critical(translate('An error has occurred'));
          if (!isAxiosError(error) || error.response.status >= 500) {
            captureException(error);
          }
        },
      }
    );
  };

  return (
    <DialogConfirmation
      actions={[
        <UIButton
          key="cancel"
          isDisabled={isLeaving}
          aria-disabled={isLeaving}
          onClick={onClose}
        >
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="leave-library"
          appearance="risky"
          isLoading={isLeaving}
          isDisabled={!membershipId}
          onClick={handleLeave}
        >
          {translate('Remove')}
        </UIButton>,
      ]}
      onClose={onClose}
      id="leave-library-dialog"
      title={translate('Leave This Library?')}
      open={isOpen}
      align="left"
    >
      {translate(
        "You'll no longer have access to {0}, its quiz library, or its reports. Any folders or quizzes you've added will remain."
      ).format(libraryName)}
    </DialogConfirmation>
  );
}
